/**
 * Dónde vive la ayuda.
 *
 * Fuera del panel a propósito, y no como una vista más. Un centro de ayuda
 * tiene que alcanzarse SIN sesión iniciada —quien no consigue entrar es justo
 * quien más lo necesita— y tiene que poder indexarse, que es por donde llega la
 * mayor parte de la gente a la ayuda de cualquier producto. Detrás del login no
 * se cumple ninguna de las dos, así que el menú enlaza al sitio y el panel no
 * guarda ninguna copia: una copia sólo podría ir por detrás del original.
 *
 * `apps/help` es un sitio hermano del panel y del editor —Astro, estático, en
 * su propio proyecto de Cloudflare Pages—, no un proyecto publicado de véltica.
 * Por eso su dirección se resuelve como la del editor y no con `publicSiteUrl`:
 * en producción es su subdominio, y en local es su servidor de desarrollo.
 */
export const HELP_SITE = (import.meta.env.VITE_HELP_URL as string | undefined)
  ?? (window.location.hostname.endsWith('veltica.com')
    ? 'https://help.veltica.com'
    : 'http://localhost:5175')

/**
 * Construye una URL hacia `help.veltica.com` respetando el idioma activo (`es` o `en`).
 */
export function helpUrl(language: 'es' | 'en' = 'es', path = ''): string {
  const base = HELP_SITE.replace(/\/+$/, '')
  const clean = path.replace(/^\/+/, '')
  const prefix = language === 'en' ? '/en' : ''
  if (!clean) return `${base}${prefix || '/'}`
  return `${base}${prefix}/${clean}`
}

/*
 * Cada pantalla del panel con su artículo. Son direcciones REALES de la ayuda
 * —`<tema>/<archivo>` de `apps/help/src/content/articles`, iguales en inglés—, y
 * `helpCenter.test.ts` comprueba que cada una exista: un enlace de ayuda que
 * acaba en 404 es peor que uno que lleva a la portada.
 */
export const SECTION_HELP_PATHS: Record<string, string> = {
  overview: 'primeros-pasos/recorrido-por-el-panel',
  pages: 'paginas-y-cms/administracion-de-paginas',
  blog: 'paginas-y-cms/crear-y-publicar-entradas-de-blog',
  cms: 'paginas-y-cms/que-es-el-cms-y-colecciones',
  forms: 'formularios-y-contactos/bandeja-de-mensajes-de-formularios',
  contacts: 'formularios-y-contactos/administrar-lista-de-contactos',
  payments: 'ventas-y-cobros/crear-tu-primera-oferta',
  digitalProducts: 'ventas-y-cobros/vender-productos-digitales',
  physicalProducts: 'ventas-y-cobros/vender-productos-fisicos-variantes-y-stock',
  services: 'ventas-y-cobros/vender-servicios-sin-agenda',
  bookings: 'reservas/crear-servicio-con-agenda-y-cobro-online',
  bookingsCalendar: 'reservas/administrar-citas-y-calendario',
  bookingsList: 'reservas/administrar-citas-y-calendario',
  orders: 'ventas-y-cobros/administrar-cobros-reembolsos-y-disputas',
  coupons: 'ventas-y-cobros/crear-y-compartir-cupones',
  shipping: 'ventas-y-cobros/configurar-envios-y-recoleccion',
  payouts: 'ventas-y-cobros/depositos-bancarios-payouts',
  analytics: 'marketing-y-analiticas/analiticas-de-visitas-del-sitio',
  salesAnalytics: 'marketing-y-analiticas/analiticas-de-ventas',
  bookingsAnalytics: 'marketing-y-analiticas/analiticas-de-reservas',
  team: 'equipo-y-permisos/invitar-a-alguien-a-tu-proyecto',
  config: 'primeros-pasos/crear-y-publicar-tu-primer-sitio',
  general: 'negocio-y-cumplimiento/region-moneda-e-idioma-del-sitio',
  brand: 'editor/estilo-de-marca-logo-colores-y-tipografias',
  business: 'negocio-y-cumplimiento/informacion-fiscal-y-de-contacto-del-negocio',
  region: 'negocio-y-cumplimiento/region-moneda-e-idioma-del-sitio',
  policies: 'negocio-y-cumplimiento/publicar-politicas-legales-del-sitio',
  publish: 'dominios-y-publicacion/publicar-despublicar-y-contrasena-del-sitio',
  usage: 'cuenta-y-facturacion/uso-y-limites-de-la-cuenta-y-del-sitio',
  backups: 'dominios-y-publicacion/copias-de-seguridad-y-restauracion',
  transfer: 'equipo-y-permisos/transferir-proyecto-a-otra-cuenta',
  stripe: 'ventas-y-cobros/conectar-y-administrar-stripe',
  googleAnalytics: 'marketing-y-analiticas/conectar-google-analytics',
  omnisend: 'formularios-y-contactos/conectar-omnisend-con-tus-formularios',
}

export const GLOBAL_VIEW_HELP_PATHS: Record<string, string> = {
  settings: 'cuenta-y-facturacion/perfil-correo-y-sesiones-activas',
  plans: 'cuenta-y-facturacion/planes-free-pro-y-business',
  usage: 'cuenta-y-facturacion/uso-y-limites-de-la-cuenta-y-del-sitio',
  billing: 'cuenta-y-facturacion/facturacion-metodos-de-pago-y-recibos',
  domains: 'dominios-y-publicacion/comprar-y-administrar-dominios-en-veltica',
}

/**
 * Devuelve la URL de ayuda contextual para la sección o vista global activa del panel.
 */
export function helpUrlForSection(
  language: 'es' | 'en' = 'es',
  section?: string | null,
  globalView?: string | null,
): string {
  const path = (globalView && GLOBAL_VIEW_HELP_PATHS[globalView])
    || (section && SECTION_HELP_PATHS[section])
    || ''
  return helpUrl(language, path)
}

